import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError, setAuthToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Storefront, CheckCircle } from "@phosphor-icons/react";

/**
 * Onboarding screen for admin-invited owners.
 *
 * Hit via the link in the "You've been added to TruckTrackr" email
 * (`/onboarding/:token`) — also reachable when an invited owner tries
 * to log in early and gets redirected here from the login page.
 *
 * Flow:
 *   1. Look up invite info by token (email, pre-filled truck name).
 *   2. User picks a name, password (+ confirm), optional phone.
 *   3. Submit → backend sets password, clears invite, logs them in.
 *   4. Toast + redirect to /dashboard so they can add their first event.
 */
export default function Onboarding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get(`/auth/invite/${token}`)
      .then(({ data }) => {
        if (!mounted) return;
        setInfo(data);
        if (data?.name) setName(data.name);
      })
      .catch((e) => {
        if (!mounted) return;
        setErrMsg(formatApiError(e.response?.data?.detail) || "This invite link is invalid or already used.");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please tell us your name");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/auth/invite/${token}/accept`, {
        password,
        name: name.trim(),
        phone: phone.trim(),
      });
      // Persist Bearer token for clients where cookies are unreliable.
      if (data?.access_token) setAuthToken(data.access_token);
      // Refresh the AuthProvider so the rest of the app sees the new user
      await refresh();
      toast.success("You're in. Let's get your first event on the map.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't finish setup");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4">
        <div className="text-sm text-muted-foreground">Loading your invite…</div>
      </div>
    );
  }

  if (errMsg || !info) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-8 text-center" data-testid="onboarding-invalid">
          <div className="font-display font-semibold text-xl">Invite link not valid</div>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            {errMsg || "This link may have expired or already been used."}
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <Link to="/login">
              <Button className="w-full" data-testid="onboarding-go-login">
                Go to login
              </Button>
            </Link>
            <Link to="/" className="text-xs text-muted-foreground hover:underline">
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10" data-testid="onboarding-page">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-5"
      >
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-primary">
            <Storefront weight="duotone" className="h-3.5 w-3.5" />
            Welcome
          </div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-1 leading-tight">
            Finish setting up{info.truck_name ? ` ${info.truck_name}` : ""}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
            We've reserved <strong className="text-foreground">{info.email}</strong> for you. Choose a password and you'll be on the map.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ob-name">Your name</Label>
          <Input
            id="ob-name"
            data-testid="onboarding-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tony Tacos"
            autoComplete="name"
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ob-pass">Password</Label>
          <Input
            id="ob-pass"
            data-testid="onboarding-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            autoComplete="new-password"
            minLength={6}
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ob-confirm">Confirm password</Label>
          <Input
            id="ob-confirm"
            data-testid="onboarding-confirm-input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={6}
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ob-phone" className="flex items-center justify-between">
            <span>Phone</span>
            <span className="text-[11px] text-muted-foreground font-normal">optional</span>
          </Label>
          <Input
            id="ob-phone"
            data-testid="onboarding-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            autoComplete="tel"
          />
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full h-11 gap-2"
          data-testid="onboarding-submit-btn"
        >
          <CheckCircle weight="bold" className="h-4 w-4" />
          {submitting ? "Setting things up…" : "Get me on the map"}
        </Button>

        <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
          By continuing you agree to keep your truck info reasonably up to date so customers don't drive to the wrong spot.
        </p>
      </form>
    </div>
  );
}
